import { createContext, useContext, useEffect } from 'react';
import { isFreeformLayout, resolveSlideShowQr } from './slideFrame.ts';

export type StageFreeformPresentationValue = {
  isFreeformSlide: boolean;
  showQr: boolean;
  setIsFreeformSlide: (value: boolean) => void;
  setShowQr: (value: boolean) => void;
};

export const StageFreeformPresentationContext =
  createContext<StageFreeformPresentationValue>({
    isFreeformSlide: false,
    showQr: true,
    setIsFreeformSlide: () => {},
    setShowQr: () => {},
  });

export function useStageFreeformPresentation() {
  return useContext(StageFreeformPresentationContext);
}

export type SlidePresentationData = {
  layout?: string | null;
  showQr?: boolean | null;
} | null;

/**
 * Keep the last known FREEFORM chrome while the next slide's data is loading.
 * Missing data does not fall back to MARKDOWN chrome.
 */
export function nextFreeformPresentation(
  previous: { isFreeformSlide: boolean; showQr: boolean },
  slideData: SlidePresentationData,
): { isFreeformSlide: boolean; showQr: boolean } {
  if (!slideData) {
    return previous;
  }
  return {
    isFreeformSlide: isFreeformLayout(slideData.layout),
    showQr: resolveSlideShowQr(slideData.showQr),
  };
}

export function useReportFreeformSlide(slideData: SlidePresentationData) {
  const { setIsFreeformSlide, setShowQr } = useStageFreeformPresentation();
  const ready = slideData != null;
  const layout = slideData?.layout;
  const showQr = slideData?.showQr;
  useEffect(() => {
    if (!ready) {
      return;
    }
    setIsFreeformSlide(isFreeformLayout(layout));
    setShowQr(resolveSlideShowQr(showQr));
  }, [ready, layout, showQr, setIsFreeformSlide, setShowQr]);

  useEffect(() => {
    return () => {
      setIsFreeformSlide(false);
      setShowQr(true);
    };
  }, [setIsFreeformSlide, setShowQr]);
}
